import { useEffect, useRef, useState, type JSX } from 'react';
import { SPECIES_META, type DuckColor, type DuckSpecies } from '@duck/contracts';

/**
 * A small, live single-{@link Critter} canvas for the Gallery showcase — the real procedural avatar
 * (WYSIWYG, the very same class the office draws), idling with its gentle breathing. Several of these tile
 * into a rich "look at the cast" wall.
 *
 * PERFORMANCE: each canvas is INTERSECTION-OBSERVED — Pixi mounts (and the ticker runs) only while the
 * card is on screen, and tears down when it scrolls away, so a wall of them never spins idle GPU. Pixi is
 * lazy-imported (off the critical path), reduced-motion safe (the Critter stills itself), and degrades to
 * the species emoji if WebGL is unavailable.
 */
export function GalleryCritter({
  species,
  color,
  activity = 'idle',
}: {
  species: DuckSpecies;
  color: DuckColor;
  /** The resting animation (mostly 'idle'; 'wave'/'dance'/'vibe' add life to the wall). */
  activity?: string;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  // Only render the live canvas while the card is on (or near) screen.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true); // no IO support → just show it (correctness over the GPU saving)
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) setVisible(e.isIntersecting);
      },
      { rootMargin: '120px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Mount Pixi only while visible; tear down when it leaves.
  useEffect(() => {
    const el = ref.current;
    if (!el || !visible) return;
    let cancelled = false;
    let handle: { destroy(): void } | null = null;

    void import('./gallery-critter-scene')
      .then(({ mountGalleryCritter }) => mountGalleryCritter(el, { species, color, activity }))
      .then((h) => {
        if (cancelled) {
          h?.destroy();
          return;
        }
        handle = h;
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      handle?.destroy();
    };
  }, [visible, species, color, activity]);

  return (
    <div
      ref={ref}
      data-gallery-critter={species}
      aria-hidden="true"
      className="relative h-full w-full"
    >
      {/* Fallback glyph behind the canvas — the only thing visible if WebGL is unavailable. */}
      <span className="absolute inset-0 flex items-center justify-center text-4xl opacity-70 sm:text-5xl">
        {SPECIES_META[species].emoji}
      </span>
    </div>
  );
}
